import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import type { AdminApi } from "../lib/api";
import { getAuthAfterLoginUrl } from "../lib/auth-callback-url";
import { authClient } from "../lib/auth-client";
import type { Session } from "../lib/auth-client";
import {
  AUTH_SESSION_VALIDATION_QUERY_KEY,
  isUnauthorizedError,
  subscribeAuthSessionExpired,
} from "../lib/auth-session";
import { AuthContext, type AuthContextValue } from "./auth-context";

interface Props {
  children: ReactNode;
  api: AdminApi;
}

function mergeVerifiedUser(
  rawSession: NonNullable<Session>,
  currentUser: Awaited<ReturnType<AdminApi["auth"]["me"]>>["data"],
): NonNullable<Session> {
  return {
    ...rawSession,
    user: {
      ...rawSession.user,
      id: currentUser.id,
      publicId: currentUser.id,
      role: currentUser.role,
      status: currentUser.status,
      name: currentUser.name,
      email: currentUser.email,
    },
  } as NonNullable<Session>;
}

export function AuthProvider({ children, api }: Props) {
  const { data: rawSession, isPending } = authClient.useSession();
  const [sessionExpired, setSessionExpired] = useState(false);
  const sessionId = rawSession?.session.id;

  const sessionValidation = useQuery({
    queryKey: [AUTH_SESSION_VALIDATION_QUERY_KEY, sessionId],
    queryFn: () => api.auth.me(),
    enabled: Boolean(rawSession),
  });

  useEffect(() => {
    setSessionExpired(false);
  }, [sessionId]);

  useEffect(() => subscribeAuthSessionExpired(() => setSessionExpired(true)), []);

  const isPendingInvite = rawSession?.user.status === "pending";
  const validationUnauthorized = isUnauthorizedError(sessionValidation.error);
  const verifiedSession =
    rawSession && sessionValidation.data
      ? mergeVerifiedUser(rawSession, sessionValidation.data.data)
      : isPendingInvite && validationUnauthorized
        ? rawSession
        : null;
  const loading = isPending || (Boolean(rawSession) && sessionValidation.isPending);
  const invalidActiveSession = Boolean(rawSession && !isPendingInvite && validationUnauthorized);
  const expired = sessionExpired || invalidActiveSession;

  const value: AuthContextValue = useMemo(
    () => ({
      session: expired ? null : verifiedSession,
      loading,
      sessionExpired: expired,
      async signInWithLine() {
        await authClient.signIn.social({ provider: "line", callbackURL: getAuthAfterLoginUrl() });
      },
      async signOut() {
        const result = await authClient.signOut();
        if (result.error || !result.data?.success) {
          throw result.error ?? new Error("Sign out response did not confirm success");
        }
        setSessionExpired(false);
      },
    }),
    [expired, loading, verifiedSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
